import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Home, Phone, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlayer, StreamStatus } from '../../contexts/PlayerContext';

export default function AppLayout({ logoSrc }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { state } = usePlayer();
  const location = useLocation();
  const isLiveOnline = state.streamStatus === StreamStatus.ONLINE;

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Navigation links
  const navigationLinks = [
    {
      path: '/',
      name: 'בית',
      icon: Home,
    },
    {
      path: '/contact',
      name: 'צור קשר',
      icon: Phone
    }
  ];

  return (
    <div className="min-h-screen bg-[#f0dadd] bg-subtle-pattern" dir="rtl">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-gradient-to-r from-[#784BCB] to-[#E15E92] shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <NavLink to="/" className="flex items-center gap-3">
              <div className="logo-container">
                {logoSrc ? (
                  <motion.img 
                    src={logoSrc} 
                    alt="רדיו שבלולים" 
                    className="w-14 h-14 drop-shadow-lg" 
                    whileHover={{ scale: 1.15, rotate: 10 }}
                    animate={{ 
                      rotate: isLiveOnline ? [0, 10, 0, -10, 0] : 0,
                      scale: isLiveOnline ? [1, 1.05, 1] : 1
                    }}
                    transition={{ 
                      duration: 5, 
                      repeat: Infinity,
                      repeatType: "mirror"
                    }}
                  />
                ) : (
                  <motion.div 
                    className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg"
                    whileHover={{ scale: 1.15, rotate: 10 }}
                    animate={{ 
                      rotate: isLiveOnline ? [0, 10, 0, -10, 0] : 0,
                      scale: isLiveOnline ? [1, 1.05, 1] : 1
                    }}
                    transition={{ 
                      duration: 5, 
                      repeat: Infinity,
                      repeatType: "mirror"
                    }}
                  >
                    <span className="text-[#784BCB] font-bold text-lg">RS</span>
                  </motion.div>
                )}
              </div>
              <motion.h1 
                className="text-3xl font-bold text-white hidden sm:block"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                רדיו שבלולים
              </motion.h1>
              
              {/* Live indicator (desktop) */}
              {isLiveOnline && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="hidden sm:flex items-center gap-2 bg-white/90 backdrop-blur-sm text-[#E15E92] text-xs px-2 py-0.5 rounded-full shadow-md"
                >
                  <motion.div 
                    animate={{ scale: [1, 1.3, 1] }} 
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="w-2 h-2 bg-[#E15E92] rounded-full" 
                  />
                  <span>שידור חי</span>
                </motion.div>
              )}
            </NavLink>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1 space-x-reverse">
              {navigationLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) => `
                    relative px-3 py-2 rounded-md text-white font-medium transition-all font-custom
                    ${isActive 
                      ? 'bg-white/20'
                      : 'hover:bg-white/10 hover:-translate-y-1'
                    }
                  `}
                >
                  {({ isActive }) => (
                    <>
                      <span className="flex items-center gap-1.5">
                        <link.icon className="w-4 h-4" />
                        {link.name}
                      </span>
                      {isActive && (
                        <motion.div 
                          layoutId="nav-underline"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Mobile menu button */}
            <motion.button 
              className="md:hidden p-2 rounded-md text-white hover:bg-white/10 app-button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              whileTap={{ scale: 0.95 }}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-16 left-0 right-0 z-40 bg-gradient-to-b from-[#784BCB] to-[#6A3DB5] md:hidden shadow-lg"
          >
            <nav className="flex flex-col p-4">
              {navigationLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <NavLink
                    to={link.path}
                    className={({ isActive }) => `
                      flex items-center gap-2 px-4 py-3 rounded-md text-white font-medium font-custom
                      ${isActive 
                        ? 'bg-white/10 shadow-md'
                        : 'hover:bg-white/5 hover:translate-x-1'
                      }
                    `}
                  >
                    <link.icon className="w-5 h-5" />
                    {link.name}
                  </NavLink>
                </motion.div>
              ))}
              
              {/* Live indicator (mobile) */}
              {isLiveOnline && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center gap-2 mt-4 bg-white/90 backdrop-blur-sm text-[#E15E92] px-4 py-3 rounded-md shadow-md"
                >
                  <motion.div 
                    animate={{ scale: [1, 1.3, 1] }} 
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="w-2 h-2 bg-[#E15E92] rounded-full" 
                  />
                  <span>שידור חי פעיל כעת</span>
                </motion.div>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="pt-20 min-h-screen">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-[#784BCB] to-[#E15E92] mt-12 py-8 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              {logoSrc ? (
                <motion.img 
                  src={logoSrc} 
                  alt="רדיו שבלולים" 
                  className="w-8 h-8" 
                  whileHover={{ scale: 1.2, rotate: 10 }}
                />
              ) : (
                <motion.div 
                  className="w-8 h-8 rounded-full bg-white flex items-center justify-center"
                  whileHover={{ scale: 1.2, rotate: 10 }}
                >
                  <span className="text-[#784BCB] font-bold text-xs app-title">RS</span>
                </motion.div>
              )}
              <span
                className="text-2xl font-bold text-white"
                style={{ fontFamily: "'Rubik', 'Heebo', system-ui, Arial, sans-serif" }}
              >
                רדיו שבלולים
              </span>
            </div>
            
            <nav className="flex gap-6">
              {navigationLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) => `
                    text-white/80 hover:text-white transition-colors relative font-custom
                    ${isActive ? 'text-white' : ''}
                  `}
                >
                  {({ isActive }) => (
                    <>
                      <motion.span whileHover={{ y: -2 }} className="inline-block">
                        {link.name}
                      </motion.span>
                      {isActive && (
                        <motion.div 
                          layoutId="footer-nav-underline"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
            
            <motion.div 
              className="text-sm text-white/85 font-medium"
              whileHover={{ opacity: 1 }}
              initial={{ opacity: 0.85 }}
            >
              כל הזכויות שמורות &copy; {new Date().getFullYear()} רדיו שבלולים
            </motion.div>
          </div>
        </div>
      </footer>
      
      {/* Floating Logo Watermark */}
      <div className="fixed bottom-5 right-5 opacity-20 pointer-events-none z-10 hidden md:block">
        {logoSrc && (
          <motion.img 
            src={logoSrc}
            alt="Watermark"
            className="w-16 h-16 filter saturate-0"
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          />
        )}
      </div>
    </div>
  );
} 